import { track } from '@amplitude/analytics-browser'
import { SkedPreview } from '@dialogueconsulting/sked-preview'
import { format } from 'date-fns'
import isEmpty from 'lodash.isempty'
import { useEffect, useState } from 'react'
import { AiFillQuestionCircle } from 'react-icons/ai'
import {
  MdOutlineKeyboardArrowDown,
  MdOutlineKeyboardArrowUp,
} from 'react-icons/md'
import Tooltip from 'react-simple-tooltip'
import styled, { css } from 'styled-components'

import { PostPreviewGrid, type PostPreviewGridProps } from 'Components'
import GlobalStyle from 'GlobalStyle'
import useKeyPress from 'hooks/useKeyPress'

import Dropdown from './Common/Dropdown'
import PoweredBySked from './Common/PoweredBySked'
import { Title } from './CommonStyleComponents'

const Container = styled.div`
  max-width: 360px;
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;

  @media (max-width: 1280px) {
    max-width: inherit;
  }
`

const FlexAlignCenterStyled = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 0 0 25px;
  align-items: center;

  > div {
    display: flex;
    align-items: center;
    font-size: 18px;
    font-weight: 700;

    span {
      margin: 0 20px;
      font-size: 14px;
      min-width: 110px;
      text-align: center;
    }
  }
`
const NavigatorButton = styled.button<{ active?: boolean }>`
  border: none;
  background: ${(props) => (props.active ? '#F6F5FF' : '#eeeeee')};
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  cursor: ${(props) => (props.active ? 'pointer' : 'normal')};

  svg {
    color: ${(props) => (props.active ? '#4f31ff' : '#333333')};
  }
`

const DraftAcconuntDropdownWrap = styled.div`
  margin-bottom: 16px;
`

const PostOrGridView = styled.div`
  display: flex;
  align-items: center;
  max-width: 360px;
  border-radius: 5px;
  background: #eaeaea;
  color: #4f31ff;
  margin: 30px auto 0;

  > div {
    padding: 14px 40px;
    border-radius: 5px;
    font-size: 12px;
    font-weight: 500;
    width: calc(360px / 2);
    text-decoration: underline;
    text-underline-offset: 3px;
    cursor: pointer;
    text-align: center;

    > .wrap {
      display: flex;
      align-items: center;
      margin-left: 10px;
      white-space: nowrap;
      position: relative;

      [data-testid='switch-to-grid-view-tooltip'] {
        left: -12px;

        > div {
          // tooltip content
          left: -90px;

          > div > div {
            // caret
            left: 78%;
          }
        }
      }
    }

    &.active {
      background: #4f31ff;
      color: #ffffff;
      text-decoration: none;
    }
  }
`

export const PostPreviewGridWrap = styled.div`
  min-height: 510px;
  overflow: hidden;

  .phone-bg {
    left: 50%;
    transform: translate(-50%);
  }

  @media (min-width: 767px) {
    .container {
      margin-top: 62px;
      min-height: 450px;
    }
  }
`

const PoweredBySkedStyled = styled(PoweredBySked)`
  padding: 30px 0;
  justify-content: center;
  display: flex;
  flex: 1;
  align-items: flex-end;
`
const PreviewPostErrorMessage = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  text-align: center;
  font-size: 14px;
  color: #888;
`

const ScheduleTimeLabel = styled.div`
  margin-bottom: 10px;
  font-size: 14px;
  font-weight: 500;
`

export type PostOrGridView = 'post' | 'grid'

export interface PostPreviewSectionProps {
  totalPostLength: number
  currentPostIndex: number
  navigatePost: (currentPostIndex: number) => void
  skedPreviewPost: any //refer to https://github.com/DialogueConsulting/sked-external-review/blob/master/src/components/post/Post.js#L14
  postOrGridView: PostOrGridView
  setpostOrGridView: React.Dispatch<React.SetStateAction<PostOrGridView>>
  isGridPreviewAvailable: boolean
  gridData: PostPreviewGridProps
}

const PostPreviewSection: React.FC<PostPreviewSectionProps> = ({
  totalPostLength,
  currentPostIndex,
  navigatePost,
  skedPreviewPost,
  postOrGridView,
  setpostOrGridView,
  isGridPreviewAvailable,
  gridData,
}) => {
  const [skedPreviewPostData, setSkedPreviewPostData] = useState<any>({})
  const [multiplePlatformAccounts, setMultiplePlatformAccounts] = useState([])
  const [selectedAccountId, setSelectedAccountId] = useState('')

  const handleNavigateButton = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      currentPostIndex = currentPostIndex - 1
    } else if (direction === 'next') {
      currentPostIndex = currentPostIndex + 1
    }

    navigatePost(currentPostIndex)
  }

  const handlePostOrGridView = (view: PostOrGridView) => () => {
    window.scrollTo(0, 0)
    setpostOrGridView(view)

    if (view === 'post') {
      track('ext-planner-click')
    }
  }

  const tooltipOptions = {
    radius: 5,
    customCss: css`
      font-weight: 700;
    `,
    placement: 'bottom',
    content: 'See how this post will look in the Instagram grid',
  }

  useEffect(() => {
    if (!isEmpty(skedPreviewPost)) {
      const hasMultiPlatform =
        skedPreviewPost.providedPost?.accounts?.length > 1

      if (hasMultiPlatform) {
        const nextMultiplePlatformAccounts =
          skedPreviewPost.providedPost.accounts.map((account: any) => ({
            id: account.id,
            label: account.name,
            value: account.id,
            avatar: account.avatar,
          }))

        setMultiplePlatformAccounts(nextMultiplePlatformAccounts)
        setSelectedAccountId(skedPreviewPost.providedPost.accounts[0].id)

        const skedPreviewPostsWithPlatform =
          skedPreviewPost.providedPost.accounts.map((account: any) => ({
            ...skedPreviewPost,
            platform: account.platform,
            _id: account.id,
            username: account.name,
            profilePicture: account.avatar,
          }))

        setSkedPreviewPostData(skedPreviewPostsWithPlatform)
      } else {
        setMultiplePlatformAccounts([])
        setSkedPreviewPostData(skedPreviewPost)
      }
    }
  }, [skedPreviewPost])

  useEffect(() => {
    setpostOrGridView('post')
  }, [currentPostIndex])

  useKeyPress('ArrowDown', () => {
    if (currentPostIndex < totalPostLength) {
      handleNavigateButton('next')
    }
  })

  useKeyPress('ArrowUp', () => {
    if (currentPostIndex > 1) {
      handleNavigateButton('prev')
    }
  })

  if (!skedPreviewPost) {
    return (
      <PreviewPostErrorMessage>
        Unable to fetch post data.
      </PreviewPostErrorMessage>
    )
  }

  if (isEmpty(skedPreviewPostData)) return null

  return (
    <Container data-testid="post-review-container">
      {postOrGridView === 'post' && (
        <>
          <FlexAlignCenterStyled>
            <Title>Post Preview</Title>
            <div>
              <NavigatorButton
                onClick={() => {
                  if (currentPostIndex > 1) {
                    handleNavigateButton('prev')
                  }
                }}
                active={currentPostIndex > 1}
              >
                <MdOutlineKeyboardArrowUp />
              </NavigatorButton>
              {currentPostIndex !== 0 && (
                <span>
                  {currentPostIndex} of {totalPostLength} Posts
                </span>
              )}
              <NavigatorButton
                onClick={() => {
                  if (currentPostIndex < totalPostLength) {
                    handleNavigateButton('next')
                  }
                }}
                active={currentPostIndex < totalPostLength}
              >
                <MdOutlineKeyboardArrowDown />
              </NavigatorButton>
            </div>
          </FlexAlignCenterStyled>

          {['queued', 'scheduled'].includes(skedPreviewPostData.stage) &&
            skedPreviewPostData.scheduledFor && (
              <ScheduleTimeLabel>
                {format(
                  new Date(skedPreviewPostData.scheduledFor),
                  'EEEE, d LLLL p'
                )}
              </ScheduleTimeLabel>
            )}

          {multiplePlatformAccounts.length > 0 && (
            <DraftAcconuntDropdownWrap>
              <Dropdown
                name="accounts"
                placeholder="Select from options"
                onChange={(selected) => setSelectedAccountId(selected.value)}
                options={multiplePlatformAccounts}
              />
            </DraftAcconuntDropdownWrap>
          )}

          <GlobalStyle />
          {multiplePlatformAccounts.length > 0 ? (
            skedPreviewPostData.map(
              (post: any) =>
                post._id === selectedAccountId && (
                  <SkedPreviewWithCover key={post._id} post={post} />
                )
            )
          ) : (
            <SkedPreviewWithCover post={skedPreviewPostData} />
          )}
        </>
      )}

      {postOrGridView === 'grid' && (
        <PostPreviewGridWrap>
          <PostPreviewGrid {...gridData} />
        </PostPreviewGridWrap>
      )}

      {isGridPreviewAvailable && (
        <PostOrGridView>
          <div
            onClick={handlePostOrGridView('post')}
            className={postOrGridView === 'post' ? 'active' : ''}
          >
            Post Preview
          </div>
          <div
            onClick={handlePostOrGridView('grid')}
            className={postOrGridView === 'grid' ? 'active' : ''}
          >
            <div className="wrap">
              <span>Grid View &nbsp;&nbsp;&nbsp;</span>
              <Tooltip
                data-testid="switch-to-grid-view-tooltip"
                {...tooltipOptions}
              >
                <AiFillQuestionCircle />
              </Tooltip>
            </div>
          </div>
        </PostOrGridView>
      )}

      <PoweredBySkedStyled />
    </Container>
  )
}

export default PostPreviewSection

const SkedPreviewWithCover = ({ post }: { post: Record<string, any> }) => {
  return (
    <SkedPreviewStyled
      post={post as any} // this needs better typing later
      className={undefined}
      env={undefined}
      trackEvent={undefined}
      videoCoverPreview={post?.providedPost?.thumbnailUrl}
    />
  )
}

const SkedPreviewStyled = styled(SkedPreview)`
  border-radius: 10px;
  padding: 3px;
  box-shadow: 3px 0 10px rgba(0, 0, 0, 0.15) !important;
  background: #ffffff;
  width: 360px;
  margin: 0 auto 18px;
`
